import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import host from "../../global";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMediaPipe } from "../../contexts/MediaPipeContext.jsx";
import Loader from "../Loader";
import BrandingFooter from "../shared/BrandingFooter.jsx";
import InstructionsDialog from "./steps/InstructionsDialog";
import { useTheme } from "../../hooks/useTheme.js";
import { useScreenProctoring } from "./hooks/useScreenProctoring.js";
import { useTutorialPrefetch } from "./hooks/useTutorialPrefetch.js";
import { updateConfigData } from "../../store/slices/miscSlice.js";
import { getPreferredMic } from "../../audio-kernel/micPreference";
import { useMediaDevices } from "../../hooks/useMediaDevices";
import { useMicrophoneTest } from "../../hooks/useMicrophoneTest";
import { useProctoringValidation } from "../../hooks/useProctoringValidation";
import { useSecondaryCamera } from "../../hooks/useSecondaryCamera";
import { TUTORIAL_VIDEO_URL } from "../../constants/secondaryCamera";
import StepProgressSidebar from "./steps/StepProgressSidebar";
import ThemeToggle from "./steps/ThemeToggle";
import MicCheckStep from "./steps/MicCheckStep";
import CameraCheckStep from "./steps/CameraCheckStep";
import ConsentStep from "./steps/ConsentStep";
import { recordConsent } from "./consentApi";
import SecondaryCameraConnectStep from "./steps/SecondaryCameraConnectStep";
import InstructionsStep from "./steps/InstructionsStep";
import ReadyStep from "./steps/ReadyStep";
import {
  STEP,
  DEVICE_PHASE,
  BASE_STEPS,
  SECONDARY_CAMERA_STEP,
  PROCTORING_INSTRUCTIONS_STEP,
  READY_STEP,
  CONSENT_POLICY_VERSION,
  ERROR_MESSAGES,
  PROCTORING_HINTS,
  POSE_VALIDATION,
  VIDEO_CONSTRAINTS,
  TIMING,
} from "../../constants/interviewInstructions";

// SECTION: Component Definition
// Shared pre-check flow for every candidate round. Everything that differs
// between rounds arrives via `config`; see PRE_CHECK_CONFIG_SHAPE below.
const PreInterviewCheck = ({ config }) => {
  const { interview_id } = useParams();
  // CandidatePortal already resolves the candidate name for its header; this
  // page reads it from the outlet instead of refetching it.
  const { candidateName } = useOutletContext() || {};
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { theme } = useTheme();
  const {
    getDetectors,
    isInitialized: isMediaPipeReady,
    isLoading: isMediaPipeLoading,
  } = useMediaPipe();

  // SUBSECTION: State Declarations
  // Interview Flow State - Manages overall progress and loading
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(STEP.CONSENT);
  const [devicePhase, setDevicePhase] = useState(DEVICE_PHASE.MIC);
  const [countdown, setCountdown] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  // True when recordConsent failed because this browser has no valid
  // candidate token for this interview (new tab/device, or the link expired)
  // -- as opposed to a plain network/server hiccup. Drives the "get a new
  // test link" action on the consent step instead of a generic retry message.
  const [isAuthError, setIsAuthError] = useState(false);
  const [showMicHelp, setShowMicHelp] = useState(false);

  // Device Testing State - Camera test status (mic status owned by useMicrophoneTest)
  const [cameraStatus, setCameraStatus] = useState("pending");

  // Consent State - one boolean per consent checkbox, index-aligned to consentItems
  const [instructionAgreements, setInstructionAgreements] = useState([]);
  const [isSavingConsent, setIsSavingConsent] = useState(false);
  const storedConsentKeyRef = useRef(null);
  const [isDeclining, setIsDeclining] = useState(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [outlineImage, setOutlineImage] = useState(null);

  // Proctoring State - Step/config flags (pose-validation state owned by useProctoringValidation)
  const [isVideoProctoringEnabled, setIsVideoProctoringEnabled] =
    useState(null);

  // Secondary Camera State - Config flag (QR/connection state owned by useSecondaryCamera)
  const [isSecondaryCameraEnabled, setIsSecondaryCameraEnabled] =
    useState(null);
  // Gates "Connect Secondary Camera" on the secondary-camera step. Held here
  // because that step unmounts when the candidate moves on — keeping it local
  // would force a re-watch of the whole tutorial every time they navigated back.
  const [tutorialWatched, setTutorialWatched] = useState(false);
  const markTutorialWatched = useCallback(() => setTutorialWatched(true), []);
  const [isTabSwitchScreenshotEnabled, setIsTabSwitchScreenshotEnabled] =
    useState(false);
  const [isLiveAvatarEnabled, setIsLiveAvatarEnabled] = useState(false);

  const { requestScreenPermission } = useScreenProctoring({
    checkInterval: 3000, // Check every 3 seconds
    autoStart: false, // Auto-start monitoring
    enabled: isTabSwitchScreenshotEnabled,
  });

  // SUBSECTION: Refs - References for DOM elements
  const videoRef = useRef(null);
  const overlayCanvasRef = useRef(null);

  // SUBSECTION: Utility Functions

  // Utility function for common error messages.
  const getErrorMessage = (type, hasMultipleMics = false) => {
    const entry = ERROR_MESSAGES[type];
    if (entry && typeof entry === "object") {
      return entry[hasMultipleMics ? "multi" : "single"];
    }
    return entry || "An error occurred. Please try again.";
  };

  // Simple hints for each proctoring requirement (no timing logic)
  const getHintForRequirement = (requirement) => {
    return (
      PROCTORING_HINTS[requirement] ||
      "Please adjust your position for better detection."
    );
  };

  const devices = useMediaDevices({ setCameraStatus, setErrorMessage });
  const {
    selectedCamera,
    setSelectedCamera,
    availableCameras,
    selectedMic,
    setSelectedMic,
    availableMics,
    stream,
    permissionsStatus,
    setPermissionsStatus,
    isRequestingCamera,
    setIsRequestingCamera,
    isRequestingMic,
    setIsRequestingMic,
    noCameraDetected,
    activeStreamsRef,
    stopMediaStream,
    stopAllStreams,
    getOrCreateStream,
    checkAndEnumerateDevices,
    requestPermissions,
  } = devices;

  const mic = useMicrophoneTest({
    interviewId: interview_id,
    selectedMic,
    setSelectedMic,
    availableMics,
    permissionsStatus,
    setPermissionsStatus,
    setErrorMessage,
    stopAllStreams,
  });
  const {
    micStatus,
    setMicStatus,
    isTestingMic,
    liveLevel,
    lastVerdict,
    testedDeviceLabel,
    testPhase,
    testMicrophone,
  } = mic;

  // One-shot restore of the device that previously passed the test — only
  // after grant (labels/ids exist), and never re-applied so it can't stomp a
  // manual dropdown choice when devicechange re-enumerates.
  const restoredPreferredMicRef = useRef(false);
  useEffect(() => {
    if (restoredPreferredMicRef.current) return;
    if (permissionsStatus.microphone !== "granted") return;
    if (!availableMics.length) return;
    restoredPreferredMicRef.current = true;
    const pref = getPreferredMic(interview_id);
    if (pref?.deviceId && availableMics.some((m) => m.deviceId === pref.deviceId)) {
      setSelectedMic(pref.deviceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsStatus.microphone, availableMics]);

  const secondaryCamera = useSecondaryCamera({
    interviewId: interview_id,
    isSecondaryCameraEnabled,
    round: config.round,
  });
  const {
    qrCode,
    qrLoading,
    qrError,
    qrThrottled,
    generateSecondaryQR,
    checkPairingStatus,
  } = secondaryCamera;

  // Pull the tutorial clip down during the steps that precede the pairing one,
  // so it is already cached by the time GatedTutorialVideo asks for it. Stops
  // on arrival at STEP.SECONDARY_CAMERA — past that the real player owns the
  // download, and two elements fetching the same URL would only split the
  // bandwidth on exactly the slow connections this is meant to help.
  useTutorialPrefetch(
    TUTORIAL_VIDEO_URL,
    isVideoProctoringEnabled &&
      isSecondaryCameraEnabled &&
      currentStep < STEP.SECONDARY_CAMERA
  );

  // SUBSECTION: Event Handlers and Navigation

  const consentItems = useMemo(() => {
    if (!isSecondaryCameraEnabled) return config.consentItems;
    return config.consentItems.map((item) =>
      item.id === "recording"
        ? {
            ...item,
            description: `${item.description}`,
          }
        : item
    );
  }, [isSecondaryCameraEnabled, config.consentItems]);

  const allConsentsAgreed =
    instructionAgreements.length === consentItems.length &&
    instructionAgreements.every((agreed) => agreed);

  const isCameraPhase =
    currentStep === STEP.DEVICES && devicePhase === DEVICE_PHASE.CAMERA;

  const hasReadyStep = config.hasReadyStep !== false;
  const instructionsStartsRound = !hasReadyStep && isVideoProctoringEnabled;
  const cameraStartsRound = !hasReadyStep && isVideoProctoringEnabled === false;

  // 1-based position in the rail, for the "Step N of M" caption
  const currentStepIndex = steps.findIndex((step) => step.id === currentStep);
  const currentStepNumber = currentStepIndex >= 0 ? currentStepIndex + 1 : null;

  const handleNextStep = async () => {
    if (currentStep === STEP.CONSENT && allConsentsAgreed) {
      // Nothing is allowed to start recording before the grant is on file, so
      // this awaits the write and keeps the candidate on this step (with a
      // retry) if it fails, rather than walking them into a recorded round.
      if (isSavingConsent) return;
      const consentKey = `${config.round}|${CONSENT_POLICY_VERSION}|${consentItems
        .map((item) => item.id)
        .join(",")}`;
      if (storedConsentKeyRef.current !== consentKey) {
        setIsSavingConsent(true);
        setErrorMessage(null);
        setIsAuthError(false);
        try {
          await recordConsent({
            interviewId: interview_id,
            round: config.round,
            consentItems,
          });
          storedConsentKeyRef.current = consentKey;
        } catch (error) {
          const authError =
            error.message === "Missing candidate token" ||
            error.message === "Interview session expired — please verify OTP again";
          setIsAuthError(authError);
          setErrorMessage(
            authError
              ? "We couldn't verify your access to this interview in this browser."
              : `${error.message}. Please check your connection and try again.`
          );
          return;
        } finally {
          setIsSavingConsent(false);
        }
      }
      setErrorMessage(null);
      setIsAuthError(false);
      setDevicePhase(DEVICE_PHASE.MIC);
      setCurrentStep(STEP.DEVICES);
    } else if (
      currentStep === STEP.DEVICES &&
      devicePhase === DEVICE_PHASE.MIC &&
      micStatus === "verified"
    ) {
      // Same step, second half — hand off from the mic to the camera.
      setErrorMessage(null);
      setDevicePhase(DEVICE_PHASE.CAMERA);
    } else if (isCameraPhase && cameraStatus === "verified") {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
      cleanup();

      if (isVideoProctoringEnabled && isSecondaryCameraEnabled) {
        setCurrentStep(STEP.SECONDARY_CAMERA);
        generateSecondaryQR();
      } else if (isVideoProctoringEnabled) {
        setCurrentStep(STEP.INSTRUCTIONS);
      } else if (cameraStartsRound) {
        startCountdown();
      } else {
        setCurrentStep(STEP.READY);
      }
    }
  };

  // Re-entering the devices step from a later step lands on the camera half,
  // which is where you left it.
  const goBackToDevices = () => {
    setDevicePhase(DEVICE_PHASE.CAMERA);
    setCurrentStep(STEP.DEVICES);
  };

  const returnToTestHome = () =>
    navigate(`/candidate/${interview_id}/testhomepage`);

  // Leaving the consent screen without answering. Nothing is recorded: they have
  // neither agreed nor refused, they just haven't decided yet.
  const handleLeaveConsent = () => {
    if (isSavingConsent || isDeclining) return;
    returnToTestHome();
  };

  const handleDeclineConsent = () => {
    if (isSavingConsent || isDeclining) return;
    setShowDeclineConfirm(true);
  };

  // Refusing is a decision, so it is recorded like any other — but it must never
  // trap the candidate here, so a failed write still lets them leave.
  const confirmDeclineConsent = async () => {
    setShowDeclineConfirm(false);
    setIsDeclining(true);
    try {
      await recordConsent({
        interviewId: interview_id,
        round: config.round,
        consentItems,
        declined: true,
      });
    } catch (error) {
      console.error("Failed to record consent decline", error);
    } finally {
      setIsDeclining(false);
    }
    returnToTestHome();
  };

  const handleBackStep = () => {
    setErrorMessage(null);
    if (currentStep === STEP.DEVICES) {
      if (devicePhase === DEVICE_PHASE.CAMERA) {
        setDevicePhase(DEVICE_PHASE.MIC);
      } else {
        setCurrentStep(STEP.CONSENT);
      }
    } else if (currentStep === STEP.SECONDARY_CAMERA) {
      goBackToDevices();
    } else if (currentStep === STEP.INSTRUCTIONS) {
      if (isSecondaryCameraEnabled) {
        setCurrentStep(STEP.SECONDARY_CAMERA);
      } else {
        goBackToDevices();
      }
    } else if (currentStep === STEP.READY) {
      if (isVideoProctoringEnabled) {
        setCurrentStep(STEP.INSTRUCTIONS);
      } else {
        goBackToDevices();
      }
    }
  };

  // Pairing is verified inside SecondaryCameraConnectStep; this only runs
  // once that check passes.
  const handleSecondaryCameraPaired = () => {
    setCurrentStep(STEP.INSTRUCTIONS);
  };

  // The handoff itself is round-specific (different APIs, different
  // destination), so it lives in config.onProceed. Everything around it —
  // loading state, stream teardown, error surfacing — is shared.
  const handleProceed = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    setIsValidationActive(false);

    const hasActiveStream = stream && stream.active;

    try {
      await config.onProceed({
        interviewId: interview_id,
        navigate,
        hasActiveStream,
        permissionsGranted:
          permissionsStatus.camera === "granted" &&
          permissionsStatus.microphone === "granted",
        isLiveAvatarEnabled,
      });

      setIsLoading(false);
      setTimeout(() => {
        cleanup();
      }, TIMING.INTERVIEW_HANDOFF_DELAY_MS);
    } catch (error) {
      console.error("Error handing off to the assessment:", error);
      setErrorMessage(
        config.proceedErrorMessage ||
          "Failed to start. Please try again or contact support."
      );
      setIsLoading(false);
      cleanup();
    }
  };

  const goFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  };

  // Fired by the Ready step. Rounds that use a countdown show it fullscreen
  // and hand off when it hits zero; the rest hand off immediately.
  const startCountdown = () => {
    if (config.shouldGoFullscreen(isVideoProctoringEnabled)) {
      goFullscreen();
    }

    if (!config.useCountdown) {
      handleProceed();
      return;
    }

    setCountdown(TIMING.COUNTDOWN_SECONDS);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleProceed();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const requestMicPermission = async () => {
    setIsRequestingMic(true);
    try {
      await getOrCreateStream('audio', selectedMic);
      await checkAndEnumerateDevices();
      // P0: no auto-start — the candidate starts the check when ready
    } catch (error) {
      console.error("Error requesting microphone permission:", error);
      setMicStatus("failed");
      let errorMessage = "";
      if (error.name === "NotAllowedError" || error.name === "SecurityError") {
        setPermissionsStatus((prev) => ({ ...prev, microphone: "denied" }));
        errorMessage = "Microphone permission denied. Please enable microphone access in your browser settings or try again.";
      } else if (error.name === "NotFoundError") {
        errorMessage = "No microphone found. Please connect a microphone and refresh the page.";
      } else if (error.name === "NotReadableError") {
        errorMessage = "Your microphone is in use by another app (like Zoom or Teams). Please close it and try again.";
      } else {
        errorMessage = "Error accessing microphone. Please check your settings and try again.";
      }
      setErrorMessage(errorMessage);
    } finally {
      setIsRequestingMic(false);
    }
  };

  const handleShareScreen = async (nextStepCallback) => {
    if (!isTabSwitchScreenshotEnabled) {
      nextStepCallback();
      return;
    }

    try {
      const screenStream = await requestScreenPermission();
      console.log("Screen sharing started:", screenStream);
      if (screenStream) {
        nextStepCallback();
      }
    } catch (error) {
      console.error("Failed to start screen sharing:", error);
    }
  };

  const requestCameraPermission = async () => {
    setIsRequestingCamera(true);
    try {
      await getOrCreateStream('video', selectedCamera);
      await checkAndEnumerateDevices();
      setTimeout(() => {
        startCamera();
      }, TIMING.DEVICE_RETRY_DELAY_MS);
    } catch (error) {
      console.error("Error requesting camera permission:", error);
      setPermissionsStatus((prev) => ({ ...prev, camera: "denied" }));
      setCameraStatus("failed");
      setErrorMessage("Camera permission denied. Please enable camera access in your browser settings or try again.");
    } finally {
      setIsRequestingCamera(false);
    }
  };

  const proctoring = useProctoringValidation({
    isVideoProctoringEnabled,
    isMediaPipeReady,
    getDetectors,
    videoRef,
    overlayCanvasRef,
    outlineImage,
    cameraStatus,
    setCameraStatus,
    setErrorMessage,
    handleNextStep,
  });
  const {
    poseDetector,
    proctoringValidation,
    setProctoringValidation,
    validationCount,
    setValidationCount,
    isValidationActive,
    setIsValidationActive,
    setValidationStartTime,
    showHints,
    setShowHints,
    initializePoseDetector,
    validateProctoringPosition,
    drawPositioningOverlay,
  } = proctoring;

  // SUBSECTION: Effects

  // Effect: Load outline image for proctoring
  useEffect(() => {
    const loadOutlineImage = () => {
      const img = new Image();
      img.onload = () => {
        setOutlineImage(img);
      };
      img.onerror = (error) => {
        console.error("Failed to load outline image:", error);
      };
      img.src = "/person_outline_image.png";
    };

    loadOutlineImage();
  }, []);


  // Effect: Fetch video proctoring configuration on mount
  useEffect(() => {
    const fetchConfiguration = async () => {
      try {
        const response = await fetch(
          `${host}/get_configurations_wo_auth/${interview_id}`
        );
        const data = await response.json();
        setIsVideoProctoringEnabled(data.video_included || false);
        setIsSecondaryCameraEnabled(data.secondary_camera_enabled || false);
        setIsTabSwitchScreenshotEnabled(data.tab_switch_screenshot || false);
        setIsLiveAvatarEnabled(data.Live_Avatar_Enabled || false);
        dispatch(
          updateConfigData({
            isTabSwitchingEnabled: data.tab_switch_screenshot || false,
            assemblyTtsEnabled: data.assembly_tts_enabled || false,
            assemblySpeechModel: data.speech_model || null,
            assemblyLanguageCodes: data.language_codes || null,
          })
        );
      } catch (error) {
        console.error("Error fetching configurations:", error);
        setIsVideoProctoringEnabled(false);
        setIsSecondaryCameraEnabled(false);
        setIsTabSwitchScreenshotEnabled(false);
        setIsLiveAvatarEnabled(false);
        dispatch(
          updateConfigData({
            isTabSwitchingEnabled: false,
            assemblyTtsEnabled: false,
            assemblySpeechModel: null,
            assemblyLanguageCodes: null,
          })
        );
      }
    };
    fetchConfiguration();
  }, [interview_id]);

  useEffect(() => {
    setInstructionAgreements(new Array(consentItems.length).fill(false));
  }, [consentItems.length]);

  // Effect: Update steps based on proctoring config
  useEffect(() => {
    if (
      isVideoProctoringEnabled !== null &&
      isSecondaryCameraEnabled !== null
    ) {
      const updatedSteps = [...BASE_STEPS];

      if (isVideoProctoringEnabled && isSecondaryCameraEnabled) {
        updatedSteps.push({ ...SECONDARY_CAMERA_STEP });
      }
      if (isVideoProctoringEnabled) {
        updatedSteps.push({ ...PROCTORING_INSTRUCTIONS_STEP });
      }
      // Final confirmation — only for rounds that end on one.
      if (hasReadyStep) {
        updatedSteps.push({ ...READY_STEP });
      }

      setSteps(updatedSteps);
    }
  }, [isVideoProctoringEnabled, isSecondaryCameraEnabled, hasReadyStep]);

  // Effect: Update step statuses dynamically.
  useEffect(() => {
    setSteps((prevSteps) =>
      prevSteps.map((step) => ({
        ...step,
        status:
          step.id < currentStep
            ? "completed"
            : step.id === currentStep
            ? "active"
            : "pending",
      }))
    );
  }, [currentStep, isVideoProctoringEnabled, isSecondaryCameraEnabled]);

  // Effect: initial permission check + devicechange listener — mount only.
  // P0 race fix: the old effect depended on the whole permissionsStatus OBJECT
  // (fresh identity on every enumeration), so devicechange/permission events
  // re-fired it, and its cleanup() killed running mic tests mid-flight — the
  // proven cause of the machine-generated retry storms (up to 219 attempts).
  useEffect(() => {
    requestPermissions();
    navigator.mediaDevices.addEventListener(
      "devicechange",
      checkAndEnumerateDevices
    );
    return () => {
      cleanup();
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        checkAndEnumerateDevices
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect: camera bring-up on the camera step — primitive deps only (never the
  // permissionsStatus object), and no cleanup teardown that could kill a
  // running test. P0: the mic test no longer auto-starts on the mic step.
  useEffect(() => {
    if (
      isCameraPhase &&
      selectedCamera &&
      permissionsStatus.camera === "granted"
    ) {
      startCamera();
      if (isVideoProctoringEnabled) {
        initializePoseDetector();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCameraPhase,
    selectedCamera,
    permissionsStatus.camera,
    isVideoProctoringEnabled,
  ]);

  //Effect: Validate candidate position to the outlinr
  useEffect(() => {
    let validationInterval;
    if (
      isCameraPhase &&
      isVideoProctoringEnabled &&
      isValidationActive &&
      poseDetector &&
      videoRef.current &&
      cameraStatus === "checking"
    ) {
      validationInterval = setInterval(
        validateProctoringPosition,
        POSE_VALIDATION.POLL_INTERVAL_MS
      ); // 5 FPS for better performance
    }

    return () => {
      if (validationInterval) {
        clearInterval(validationInterval);
      }
    };
  }, [
    isCameraPhase,
    isVideoProctoringEnabled,
    isValidationActive,
    poseDetector,
    cameraStatus,
  ]);

  useEffect(() => {
    if (!isCameraPhase || !isVideoProctoringEnabled) {
      setIsValidationActive(false);
      setValidationCount(0);
      setValidationStartTime(null);
      setShowHints(false);
      setProctoringValidation({
        handsVisible: false,
        upperBodyVisible: false,
        properPosture: false,
        faceVisible: false,
        properDistance: false,
      });
    }
  }, [isCameraPhase, isVideoProctoringEnabled]);

  useEffect(() => {
  if (permissionsStatus.microphone === "denied" || permissionsStatus.microphone === "prompt") {
    // Only clear error message, keep micStatus so UI state is preserved
    setErrorMessage(null);
  }
}, [permissionsStatus.microphone]);

  //SUBSECTION: Cleanup

  //Cleans up resources on unmount or state change.
  const cleanup = () => {
    console.log('CLEANUP - Starting comprehensive cleanup');
    mic.cleanupAudio();
    proctoring.resetValidationState();
    stopAllStreams();
    console.log('CLEANUP - Complete');
  };

  // Call cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      stopAllStreams();
      proctoring.disposePoseDetector();
    };
  }, [poseDetector]);

  const startCamera = async () => {
    if (noCameraDetected) {
      setCameraStatus("failed");
      setErrorMessage(
        "No camera detected. Please connect a camera and try again."
      );
      return;
    }

    try {
      setCameraStatus("checking");
      if (activeStreamsRef.current.video) {
      stopMediaStream(activeStreamsRef.current.video);
      activeStreamsRef.current.video = null;
    }
      cleanup();

      const videoStream = await getOrCreateStream('video', selectedCamera);

      if (videoRef.current) {
        videoRef.current.srcObject = videoStream;

        if (overlayCanvasRef.current && outlineImage) {
          const canvas = overlayCanvasRef.current;
          canvas.width = VIDEO_CONSTRAINTS.WIDTH_IDEAL;
          canvas.height = VIDEO_CONSTRAINTS.HEIGHT_IDEAL;
          drawPositioningOverlay(canvas, null, proctoringValidation, videoRef.current);
        }

        videoRef.current.onloadedmetadata = async () => {
          setErrorMessage(null);

          if (overlayCanvasRef.current && videoRef.current) {
            const canvas = overlayCanvasRef.current;
            canvas.width = videoRef.current.videoWidth || VIDEO_CONSTRAINTS.WIDTH_IDEAL;
            canvas.height = videoRef.current.videoHeight || VIDEO_CONSTRAINTS.HEIGHT_IDEAL;

            drawPositioningOverlay(
              overlayCanvasRef.current,
              null,
              proctoringValidation,
              videoRef.current
            );
          }

          if (isVideoProctoringEnabled) {
            console.log("Proctoring mode: Starting pose validation");
            await initializePoseDetector();
            setIsValidationActive(true);
          } else {
            console.log("Simple mode: Camera verified immediately");
            setCameraStatus("verified");
          }
        };
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      setCameraStatus("failed");
      setPermissionsStatus((prev) => ({ ...prev, camera: "denied" }));
      setErrorMessage(getErrorMessage("cameraNotWorking"));
    }
  };

  // Loader
  if (isLoading) {
    return <Loader iswhitelabelenable={theme === "light"} />;
  }

  if (countdown !== null) {
    return (
      <div
        className={`fixed inset-0 z-50 flex items-center justify-center ${
          theme === "light" ? "bg-[#F0F2FB] text-slate-900" : "bg-gray-900 text-white"
        }`}
      >
        <div
          className={`text-3xl font-normal ${
            theme === "light" ? "text-gray-700" : "text-[#A95BCF]"
          }`}
        >
          Starting in {countdown}...
        </div>
      </div>
    );
  }

  return (
    <div
      className={`interview-shell flex flex-col justify-between ${
        theme === "light" ? "bg-[#FAF9F6] text-slate-900" : " text-white "
      }`}
    >
      {/* Fit everything inside the viewport — never scroll, scale down on short screens */}
      <style>{`
        html, body { overflow: hidden; }
        .interview-shell {
          /* zoom shrinks the content; the compensating height makes it fill the
            viewport exactly so there's no scroll and no empty gap at the bottom */
          height: calc(100dvh / var(--ui-zoom, 1));
          overflow: hidden;
          zoom: var(--ui-zoom, 1);
        }
        @media (max-height: 900px) { .interview-shell { --ui-zoom: 0.92; } }
        @media (max-height: 820px) { .interview-shell { --ui-zoom: 0.85; } }
        @media (max-height: 740px) { .interview-shell { --ui-zoom: 0.80; } }
        @media (max-height: 660px) { .interview-shell { --ui-zoom: 0.74; } }
        @media (max-height: 580px) { .interview-shell { --ui-zoom: 0.68; } }
      `}</style>
      {theme === "light" && (
        <style>
          {`
        /* For Chrome, Edge, Safari */
        ::-webkit-scrollbar {
          width: 10px;
        }

        ::-webkit-scrollbar-track {
          background: #e0e0e0; /* gray track */
        }

        ::-webkit-scrollbar-thumb {
          background-color: #FAF9F6; /* white thumb */
          border-radius: 10px;
          border: 2px solid #e0e0e0; /* optional: blends thumb into track */
        }

        /* Firefox */
        * {
          scrollbar-width: thin;
          scrollbar-color: #FAF9F6 #e0e0e0; /* thumb color | track color */
        }
      `}
        </style>
      )}
      {/* Show loading state while configuration is being fetched */}
      {isVideoProctoringEnabled === null ? (
        <div className="flex justify-center items-center flex-1">
          <div className="text-center">
            <RefreshCw
              className={`w-8 h-8 animate-spin mx-auto mb-2 ${
                theme === "light" ? "text-gray-700" : "text-white"
              }`}
            />
            <p className="text-[#B3B3B3]">
              Preparing your interview experience...
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 min-h-0">
          {/* Step rail — only render once configuration has resolved */}
          {steps.length > 0 && (
            <StepProgressSidebar
              steps={steps}
              candidateName={candidateName}
              interviewId={interview_id}
              title={config.railTitle}
              showSecondaryCameraHelp={Boolean(
                isVideoProctoringEnabled && isSecondaryCameraEnabled
              )}
            />
          )}

          <main
            className={`flex-1 min-w-0 min-h-0 flex flex-col overflow-y-auto ${
              theme === "light" ? "bg-[#F0F2FB]" : ""
            }`}
          >
            {/* Replaces the portal header, which used to own the theme switch */}
            <div className="flex justify-end px-8 pt-6 shrink-0">
              <ThemeToggle />
            </div>

            <div className="flex-1 flex items-start justify-center px-8 pt-16 pb-8">
              <div className="w-full max-w-4xl">
              {currentStep === STEP.CONSENT ? (
                <ConsentStep
                  candidateName={candidateName}
                  consentItems={consentItems}
                  agreements={instructionAgreements}
                  setAgreements={setInstructionAgreements}
                  stepNumber={currentStepNumber}
                  totalSteps={steps.length}
                  onContinue={handleNextStep}
                  onBack={handleLeaveConsent}
                  onDecline={handleDeclineConsent}
                  isSaving={isSavingConsent}
                  isDeclining={isDeclining}
                  errorMessage={errorMessage}
                  isAuthError={isAuthError}
                  onGetNewLink={() =>
                    navigate(`/candidate/${interview_id}/scheduletest`, { replace: true })
                  }
                />
              ) : currentStep === STEP.DEVICES &&
                devicePhase === DEVICE_PHASE.MIC ? (
                <MicCheckStep
                  showMicHelp={showMicHelp}
                  setShowMicHelp={setShowMicHelp}
                  setErrorMessage={setErrorMessage}
                  errorMessage={errorMessage}
                  setMicStatus={setMicStatus}
                  micStatus={micStatus}
                  testMicrophone={testMicrophone}
                  permissionsStatus={permissionsStatus}
                  requestMicPermission={requestMicPermission}
                  isRequestingMic={isRequestingMic}
                  selectedMic={selectedMic}
                  setSelectedMic={setSelectedMic}
                  availableMics={availableMics}
                  isTestingMic={isTestingMic}
                  liveLevel={liveLevel}
                  lastVerdict={lastVerdict}
                  testedDeviceLabel={testedDeviceLabel}
                  testPhase={testPhase}
                  handleNextStep={handleNextStep}
                  onBack={handleBackStep}
                  stepNumber={currentStepNumber}
                  totalSteps={steps.length}
                />
              ) : isCameraPhase ? (
                <CameraCheckStep
                  isVideoProctoringEnabled={isVideoProctoringEnabled}
                  noCameraDetected={noCameraDetected}
                  permissionsStatus={permissionsStatus}
                  requestCameraPermission={requestCameraPermission}
                  isRequestingCamera={isRequestingCamera}
                  errorMessage={errorMessage}
                  cameraStatus={cameraStatus}
                  videoRef={videoRef}
                  overlayCanvasRef={overlayCanvasRef}
                  outlineImage={outlineImage}
                  poseDetector={poseDetector}
                  isMediaPipeLoading={isMediaPipeLoading}
                  showHints={showHints}
                  setShowHints={setShowHints}
                  proctoringValidation={proctoringValidation}
                  validationCount={validationCount}
                  getHintForRequirement={getHintForRequirement}
                  selectedCamera={selectedCamera}
                  setSelectedCamera={setSelectedCamera}
                  availableCameras={availableCameras}
                  handleNextStep={handleNextStep}
                  onBack={handleBackStep}
                  stepNumber={currentStepNumber}
                  totalSteps={steps.length}
                  continueLabel={
                    cameraStartsRound
                      ? config.ready?.startButtonLabel
                      : undefined
                  }
                />
              ) : currentStep === STEP.SECONDARY_CAMERA ? (
                <SecondaryCameraConnectStep
                  qrLoading={qrLoading}
                  qrError={qrError}
                  qrCode={qrCode}
                  qrThrottled={qrThrottled}
                  generateSecondaryQR={generateSecondaryQR}
                  checkPairingStatus={checkPairingStatus}
                  onPaired={handleSecondaryCameraPaired}
                  onBack={handleBackStep}
                  tutorialWatched={tutorialWatched}
                  onTutorialWatched={markTutorialWatched}
                  stepNumber={currentStepNumber}
                  totalSteps={steps.length}
                />
              ) : currentStep === STEP.INSTRUCTIONS &&
                isVideoProctoringEnabled ? (
                <InstructionsStep
                  isTabSwitchScreenshotEnabled={isTabSwitchScreenshotEnabled}
                  handleShareScreen={handleShareScreen}
                  instructions={config.instructions}
                  onComplete={() =>
                    hasReadyStep ? setCurrentStep(STEP.READY) : startCountdown()
                  }
                  completeLabel={
                    instructionsStartsRound
                      ? config.ready?.startButtonLabel
                      : undefined
                  }
                  onBack={handleBackStep}
                  stepNumber={currentStepNumber}
                  totalSteps={steps.length}
                />
              ) : currentStep === STEP.READY ? (
                <ReadyStep
                  completedSteps={steps.filter(
                    (step) => step.id !== STEP.READY
                  )}
                  onStart={startCountdown}
                  onBack={handleBackStep}
                  headline={config.ready.headline}
                  startButtonLabel={config.ready.startButtonLabel}
                  checklist={config.ready.checklist}
                  stepNumber={currentStepNumber}
                  totalSteps={steps.length}
                />
              ) : null}
              </div>
            </div>

            <div className="shrink-0 pb-5">
              <BrandingFooter />
            </div>
          </main>
        </div>
      )}

      <InstructionsDialog
        open={showDeclineConfirm}
        onClose={() => setShowDeclineConfirm(false)}
        title="Decline Consent"
        message="If you decline, you will not be able to take this interview. Are you sure you want to decline?"
        confirmText="Decline"
        cancelText="Cancel"
        tone="danger"
        onConfirm={confirmDeclineConsent}
      />
    </div>
  );
};

export default PreInterviewCheck;
